import React, {
  useCallback,
  useState,
  useEffect,
  useMemo,
  useRef,
  memo,
  useContext,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Buffer } from 'buffer';
import { Event, SocketContext } from '../../socketProvider';
import TakeSnapshotButton from './TakeSnapshotButton';
import GridImageViewer from './GridImageViewer';
import MapGridMosaic from './positioningForm/Tabs/MapGridMosaic';
import { updatePaths } from './actions';
import { socketEventNames } from './helper';
import camMap from './mapLogic';
import { getRowDict } from './acsSeatMath';
import {
  mappedRegionsOnMosaic,
  mosaicPickPathUpdates,
  mosaicPixelToPtz,
  ptzToMapRelative,
} from './mapGridGeometry';
import { CORNER_COLOR, crosshairCursor } from './positioningForm/CornerControls';
import { displayErrorModal } from '../../helpers/swal';
import {
  buildPointClickMoveCmd,
  getClickCoordsInElement,
  PTZ_MODEL_HANWHA,
  PTZ_MODEL_ONVIF,
} from './roverPtz';

import style from './style.module.scss';

const {
  ROVER_MOVE,
  ROVER_GET_POSITION,
  ROVER_JOIN_LIVESTREAM,
  ROVER_LEAVE_LIVESTREAM,
} = socketEventNames;

const getCropCoordinates = (angleInRadians, imageDimensions) => {
  const ang = angleInRadians;
  const img = imageDimensions;

  // eslint-disable-next-line no-bitwise
  const quadrant = Math.floor(ang / (Math.PI / 2)) & 3;
  // eslint-disable-next-line no-bitwise
  const signAlpha = (quadrant & 1) === 0 ? ang : Math.PI - ang;
  const alpha = ((signAlpha % Math.PI) + Math.PI) % Math.PI;

  const bb = {
    w: img.w * Math.cos(alpha) + img.h * Math.sin(alpha),
    h: img.w * Math.sin(alpha) + img.h * Math.cos(alpha),
  };

  const gamma = img.w < img.h ? Math.atan2(bb.w, bb.h) : Math.atan2(bb.h, bb.w);

  const delta = Math.PI - alpha - gamma;

  const length = Math.max(img.w, img.h);
  const d = length * Math.cos(alpha);
  const a = (d * Math.sin(alpha)) / Math.sin(delta);

  const y = a * Math.cos(gamma);
  const x = y * Math.tan(gamma);

  return {
    x: Math.floor(x),
    y: Math.floor(y),
    w: Math.floor(bb.w - 2 * x),
    h: Math.floor(bb.h - 2 * y),
  };
};


const StreamPreview = () => {
  const socket = useContext(SocketContext);
  const dispatch = useDispatch();
  const containerRef = useRef();
  const imageRef = useRef();
  const connectedCamera = useSelector(({ mapping: { connectedCamera: cam } }) => cam);
  const hanwhaClickToMove = useSelector(
    ({ mapping: { preview: { hanwhaClickToMove: enabled } } }) => enabled,
  );
  const ptzModel = hanwhaClickToMove ? PTZ_MODEL_HANWHA : PTZ_MODEL_ONVIF;
  const panStepSpeed = useSelector(
    ({ mapping: { preview: { stepSpeed: { pan: { value } } } } }) => value,
  );
  const tiltStepSpeed = useSelector(
    ({ mapping: { preview: { stepSpeed: { tilt: { value } } } } }) => value,
  );
  const updatePositionOnMove = useSelector(
    ({ mapping: { preview: { continuousSpeed: { updatePosition } } } }) => updatePosition,
  );
  const angle = useSelector(({ mapping: { preview: { angle: { value } } } }) => value);
  const referenceImage = useSelector(({ mapping: { preview: { referenceImage: img } } }) => img);
  const overlay = useSelector(({ mapping: { overlayURL } }) => overlayURL);
  const viewingRegionId = useSelector(({ mapping: { grid } }) => grid.viewingRegionId);
  const gridRegions = useSelector(({ mapping: { grid } }) => grid.regions);
  const viewingRegion = viewingRegionId !== null
    ? gridRegions.find((region) => region.regionId === viewingRegionId)
    : null;
  const mapGridMosaicUrl = useSelector(({ mapping: { mapGrid } }) => mapGrid.mosaicUrl);
  const mapGridMosaicWidth = useSelector(({ mapping: { mapGrid } }) => mapGrid.mosaicWidth);
  const mapGridMosaicHeight = useSelector(({ mapping: { mapGrid } }) => mapGrid.mosaicHeight);
  const mapGridCreating = useSelector(({ mapping: { mapGrid } }) => mapGrid.creating);
  const mapGridGeometry = useSelector(({ mapping: { mapGrid } }) => mapGrid.grid);
  const mapGridActiveCorner = useSelector(({ mapping: { mapGrid } }) => mapGrid.activeCorner);
  const crosshairCorner = useSelector(({ mapping }) => ({
    map: mapping.map.activeCorner,
    check: mapping.check.activeCorner,
    mapGrid: mapping.mapGrid.activeCorner,
  }[mapping.selectedTab]));
  const crosshairColor = CORNER_COLOR[crosshairCorner] || '#ffffff';
  const mapGridRegionType = useSelector(({ mapping: { mapGrid } }) => mapGrid.regionType);
  const mapGridTopLeft = useSelector(({ mapping: { mapGrid } }) => mapGrid.topLeft);
  const mapGridTopRight = useSelector(({ mapping: { mapGrid } }) => mapGrid.topRight);
  const mapGridBottomLeft = useSelector(({ mapping: { mapGrid } }) => mapGrid.bottomLeft);
  const mapGridBottomRight = useSelector(({ mapping: { mapGrid } }) => mapGrid.bottomRight);
  const mapGridCorners = useMemo(() => ({
    topLeft: mapGridTopLeft,
    topRight: mapGridTopRight,
    bottomLeft: mapGridBottomLeft,
    bottomRight: mapGridBottomRight,
  }), [mapGridTopLeft, mapGridTopRight, mapGridBottomLeft, mapGridBottomRight]);
  const loadingStatus = useSelector(({ mapping: { loadingStatus: status } }) => status);
  const mappedRegions = useMemo(
    () => mappedRegionsOnMosaic(mapGridGeometry, {
      sections: camMap.getCamMapSections(),
      rowDict: getRowDict(),
      reference: camMap.getReference(),
    }),
    [mapGridGeometry, loadingStatus],
  );
  const [cropCoordinates, setCropCoordinates] = useState();
  const frameRateRef = useRef();
  const frameTimestamp = useRef();
  const clickPositionTimeoutRef = useRef();
  const lastClickMoveAtRef = useRef(0);
  const pausedLivestreamRef = useRef(false);
  const isNonLivePreview = Boolean(mapGridMosaicUrl) || Boolean(viewingRegion);

  // Stop liveMap frames while mosaic / grid view is showing; resume on return.
  useEffect(() => {
    if (!connectedCamera || !socket) {
      pausedLivestreamRef.current = false;
      return undefined;
    }

    if (isNonLivePreview) {
      socket.emit(ROVER_LEAVE_LIVESTREAM, 'map', connectedCamera);
      pausedLivestreamRef.current = true;
      return undefined;
    }

    if (pausedLivestreamRef.current) {
      pausedLivestreamRef.current = false;
      socket.emit(ROVER_JOIN_LIVESTREAM, 'map', connectedCamera, (joined) => {
        if (joined) {
          socket.emit(ROVER_GET_POSITION, 'host', connectedCamera, '#');
        }
      });
    }

    return undefined;
  }, [connectedCamera, isNonLivePreview, socket]);

  const handleStreamClick = useCallback((event) => {
    if (!connectedCamera || !socket || !imageRef.current) {
      return;
    }
    const now = Date.now();
    // Debounce rapid clicks so ACS/API are not flooded with duplicate moves.
    if (now - lastClickMoveAtRef.current < 250) {
      return;
    }
    lastClickMoveAtRef.current = now;

    const coords = getClickCoordsInElement(event, imageRef.current, angle);
    const moveCmd = buildPointClickMoveCmd({
      ptzModel,
      ...coords,
      panSpeed: panStepSpeed,
      tiltSpeed: tiltStepSpeed,
    });
    if (!moveCmd) {
      return;
    }
    socket.emit(ROVER_MOVE, 'host', connectedCamera, moveCmd);
    if (updatePositionOnMove) {
      clearTimeout(clickPositionTimeoutRef.current);
      clickPositionTimeoutRef.current = setTimeout(() => {
        socket.emit(ROVER_GET_POSITION, 'host', connectedCamera, '#');
      }, 750);
    }
  }, [
    connectedCamera,
    socket,
    hanwhaClickToMove,
    panStepSpeed,
    tiltStepSpeed,
    updatePositionOnMove,
    angle,
  ]);

  const handleMosaicClick = useCallback((mosaicX, mosaicY) => {
    if (mapGridCreating || !connectedCamera) return;
    const hit = mosaicPixelToPtz(mapGridGeometry, mosaicX, mosaicY);
    if (!hit) {
      displayErrorModal({ text: 'This mosaic has no grid data. Create a new mosaic to pick corners.' });
      return;
    }
    const { panRel, tiltRel } = ptzToMapRelative(hit.pan, hit.tilt, camMap.getReference());
    dispatch(updatePaths(mosaicPickPathUpdates({
      corner: mapGridActiveCorner || 'topLeft',
      panRel,
      tiltRel,
      mosaicX,
      mosaicY,
      regionType: mapGridRegionType,
      corners: mapGridCorners,
      grid: mapGridGeometry,
    })));
  }, [
    mapGridCreating,
    mapGridGeometry,
    mapGridActiveCorner,
    mapGridRegionType,
    mapGridCorners,
    connectedCamera,
    dispatch,
  ]);

  const liveMapHandler = useCallback((arrayBuffer) => {
    const base64String = Buffer.from(arrayBuffer).toString('base64');
    imageRef.current.src = `data:image/jpeg;base64,${base64String}`;

    const currentTime = performance.now();
    if (frameTimestamp?.current) {
      const deltaTimeInSeconds = (currentTime - frameTimestamp.current) / 1000;
      frameRateRef.current.innerHTML = deltaTimeInSeconds.toFixed(3);
    }
    frameTimestamp.current = currentTime;
  }, []);

  useEffect(() => {
    if (imageRef?.current) {
      imageRef.current.src = '';
    }
  }, [connectedCamera]);

  useEffect(() => () => clearTimeout(clickPositionTimeoutRef.current), []);

  useEffect(() => {
    const updateCropCoordinates = () => {
      if (containerRef.current) {
        const height = containerRef.current.offsetHeight;
        if (angle === 0) {
          setCropCoordinates({ w: height, h: height });
        }
        const rotationAngle = (-1 * angle) * (Math.PI / 180);
        setCropCoordinates(getCropCoordinates(rotationAngle, { w: height, h: height }));
      }
    };

    // Initial call
    updateCropCoordinates();

    let resizeObserver;
    if (typeof ResizeObserver !== 'undefined') {
      // Update on resize
      resizeObserver = new ResizeObserver(() => updateCropCoordinates());
      resizeObserver.observe(containerRef.current);
    }

    return () => {
      if (resizeObserver) {
        resizeObserver.unobserve(containerRef.current);
      }
    };
  }, [angle]);

  if (mapGridMosaicUrl) {
    return (
      <div ref={containerRef} className={style.streamContainer}>
        <MapGridMosaic
          mosaicUrl={mapGridMosaicUrl}
          mosaicWidth={mapGridMosaicWidth}
          mosaicHeight={mapGridMosaicHeight}
          corners={mapGridCorners}
          activeCorner={mapGridActiveCorner}
          mappedRegions={mappedRegions}
          disabled={mapGridCreating || !connectedCamera}
          onClose={() => dispatch(updatePaths({
            'mapGrid.mosaicUrl': null,
          }))}
          onMosaicClick={handleMosaicClick}
        />
      </div>
    );
  }

  if (viewingRegion) {
    return (
      <div ref={containerRef} className={style.streamContainer}>
        <span className={style.gridViewLabel}>Grid view</span>
        <GridImageViewer region={viewingRegion} />
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className={style.streamContainer}
    >
      {(connectedCamera && cropCoordinates) && (
        <div
          className={style.cropBox}
          style={{
            width: cropCoordinates.w,
            height: cropCoordinates.h,
            borderWidth: `${cropCoordinates.h}px`,
          }}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 448 512"
            style={CORNER_COLOR[crosshairCorner] ? { fill: crosshairColor } : undefined}
          >
            <path d="M232 72c0-4.4-3.6-8-8-8s-8 3.6-8 8V248H40c-4.4 0-8 3.6-8 8s3.6 8 8 8H216V440c0 4.4 3.6 8 8 8s8-3.6 8-8V264H408c4.4 0 8-3.6 8-8s-3.6-8-8-8H232V72z" />
          </svg>
          <div style={{ transform: `scale(${1 / process.env.DEFAULT_SCALE})` }}>
            {overlay && <img className={style.overlay} src={overlay} alt="overlay" />}
          </div>
        </div>
      )}
      {connectedCamera && <TakeSnapshotButton cam={connectedCamera} />}
      <div className={style.stream} style={{ transform: `rotateZ(${angle}deg)` }}>
        <Event event="liveMap" handler={liveMapHandler} />
        <span ref={frameRateRef} className={style.frameRateOverlay} />
        {connectedCamera ? (
          <button
            type="button"
            className={style.streamClickable}
            style={CORNER_COLOR[crosshairCorner]
              ? { cursor: crosshairCursor(crosshairColor) }
              : undefined}
            onClick={handleStreamClick}
            title="Click to move camera to this point"
            aria-label="Click to move camera to this point"
          >
            <img ref={imageRef} src="" alt="Preview" id="streamPreview" />
          </button>
        ) : (
          <img ref={imageRef} src="" alt="Preview" id="streamPreview" />
        )}
        {referenceImage && <img src={referenceImage} className={style.referenceImg} alt="reference" />}
      </div>
    </div>
  );
};

export default memo(StreamPreview);
